// @owners { team: patients }
import {
  Body,
  Button,
  Card,
  InputPassword,
  InputText,
  LgPadding,
  LoadingEmptyState,
  SecondaryPage,
  Toast,
  ToastContext,
  XlPadding,
  XlSpacing,
} from '@alto/design-system';
import { useQueryCreateAccountConfigs, useQuerySuccessConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { updateOnboardingUser } from '~shared/actions/onboarding';
import { getCompanyName } from '~shared/config/getCompanyName';
import { PASSWORD_ERROR_MESSAGE, getVerifyFailureErrorMessage } from '~shared/constants';
import {
  CREATE_ACCOUNT,
  FERTILITY_SUPPORT,
  ONBOARD_STATE_DONE,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getError from '~shared/features/ui/selectors/getErrors';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getIsCurrentUserFertility } from '~shared/features/users/selectors/getIsFertilityUser';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { useFeatureDisablingMapping } from '~shared/hooks/useIsFeatureDisabledForCompany';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { validateEmail, validatePassword, validatePresentWithMessage } from '~shared/validations';
import { useNavigationContext } from '~web/contexts';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';
import { previousUrl as getOnboardingPreviousUrl, nextUrl } from '~web/features/onboarding/helpers/urlHandler';

export type Values = {
  email: string;
  password: string;
};

const defaultValues: Values = {
  email: '',
  password: '',
};

export const OnboardingCreateAccountPage = () => {
  const { navigate } = useNavigation();
  const location = useLocation();
  const { addToast } = useContext(ToastContext);
  const { updateOnboardingUserLoading } = useSelectorShared(getLoading);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const showFertility = useSelectorShared(getIsCurrentUserFertility);
  const { trackPageView } = useAnalytics();
  const dispatch = useDispatchShared();
  const { previous: previousWebUrl } = useNavigationContext();
  const origin = previousWebUrl ? routeFromPath(previousWebUrl) : '';
  const currentRoute = routeFromPath(location.pathname);
  const { disabledFeatures, isLoading } = useFeatureDisablingMapping();
  const shouldDisableUnboxingGuide = disabledFeatures.unboxing_guide;
  const shouldDisableInstructionalVideos = disabledFeatures.app_instructional_videos;

  const shouldSkipFertilitySupport = shouldDisableUnboxingGuide || shouldDisableInstructionalVideos;
  const showFertilitySupport = (showProgyny || showFertility) && !shouldSkipFertilitySupport;
  const nextRoute = nextUrl({
    currentRoute: CREATE_ACCOUNT,
    desiredRoute: showFertilitySupport ? FERTILITY_SUPPORT : undefined,
  });
  const { hasInsurance } = useGetInsurances();
  const onboardingPreviousUrl = getOnboardingPreviousUrl({ params: { hasInsurance }, currentRoute });
  const { updateOnboardingUserError } = useSelectorShared(getError);
  const { headerBackgroundColor, headerLogoUrl, isFetching, partnershipName } = useQueryCreateAccountConfigs();

  // Prefetch onboarding success config before account creation.
  useQuerySuccessConfigs();

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__CREATE_LOGIN_VIEWED,
      params: { origin, is_progyny_rx_view: showProgyny },
    });
  }, [trackPageView, showProgyny, origin]);

  const {
    control,
    formState: { isSubmitted },
    handleSubmit,
  } = useForm<Values>({
    defaultValues,
    mode: 'onSubmit',
  });

  const emailController = useController({
    control,
    name: 'email',
    rules: {
      validate: (v) => validatePresentWithMessage('Email')(v) || validateEmail(v) || true,
    },
  });

  const passwordController = useController({
    control,
    name: 'password',
    rules: {
      validate: (v) => validatePresentWithMessage('Password')(v) || validatePassword(v) || true,
    },
  });

  const handleBack = () => {
    if (onboardingPreviousUrl) {
      navigate(onboardingPreviousUrl);
    }
  };

  const handleSave = async (data: Values) => {
    // IMPORTANT! This is the last step tracked step of onboarding.
    // Don't call `updateOnboardingUser` after this page because the patient is
    // re-issued a token and navigates to the homescreen after any additional information pages
    // (i.e. FertilitySupport for progyny and fertility patients)
    // eslint-disable-next-line import/no-deprecated
    const success = await dispatch(updateOnboardingUser({ ...data, onboard_state: ONBOARD_STATE_DONE }));
    if (success) {
      navigate(nextRoute);
    } else {
      addToast(
        <Toast variant="error">{getErrorMessage(updateOnboardingUserError) || getVerifyFailureErrorMessage()}</Toast>,
      );
    }
  };

  const titleText = showProgyny ? `Create your ${getCompanyName()} login` : 'Create your account login';

  if (isFetching || isLoading) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      withoutWebFramingElementHeights
      buttons={[
        <Button
          type="primary"
          key="finish"
          label="Finish"
          onPress={handleSubmit(handleSave)}
          loading={(updateOnboardingUserLoading && isSubmitted) ?? false}
        />,
      ]}
      footerPlacementContext="webScreenWithoutPolicyFooter"
      dismissIcon="chevronleft"
      onDismiss={handleBack}
      headerBackgroundColor={headerBackgroundColor}
      HeaderContent={
        <OnboardingHeader
          title={titleText}
          subtitle={
            <Body center>You'll use this email address and password to log into your {getCompanyName()} account.</Body>
          }
        />
      }
      NavBarCenterContent={
        <OnboardingNavBarLogo
          illustrationSrc={headerLogoUrl}
          partnershipName={partnershipName}
        />
      }
    >
      {updateOnboardingUserLoading ? (
        <LoadingEmptyState />
      ) : (
        <Card>
          <LgPadding>
            <InputText
              id="email"
              accessibilityLabel="Email address"
              label="Email address"
              value={emailController.field.value}
              placeholder="username@email.com"
              autoCapitalize="none"
              error={isSubmitted ? emailController.fieldState.error?.message : undefined}
              onChangeText={emailController.field.onChange}
              onBlur={emailController.field.onBlur}
              required
            />
            <XlSpacing />
            <InputPassword
              accessibilityLabel="password"
              label="Create password"
              value={passwordController.field.value}
              error={isSubmitted ? passwordController.fieldState.error?.message : undefined}
              onChangeText={passwordController.field.onChange}
              onBlur={passwordController.field.onBlur}
              placeholder="Enter password"
              caption={PASSWORD_ERROR_MESSAGE}
            />
          </LgPadding>
        </Card>
      )}
    </SecondaryPage>
  );
};
